import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  leadsApi,
  type ProjectBriefSummary,
  type ProjectBriefDetail,
  type ProjectBriefStatus,
  type ContactSubmission,
  type ContactStatus,
} from './api/leadsApi';

type TabKey = 'call' | 'waitlist' | 'general';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'call', label: 'Discovery calls' },
  { key: 'waitlist', label: 'Waitlist' },
  { key: 'general', label: 'General enquiries' },
];

const BRIEF_STATUSES: ProjectBriefStatus[] = ['NEW', 'REVIEWED', 'IN_DISCUSSION', 'CLOSED'];
const CONTACT_STATUSES: ContactStatus[] = ['NEW', 'IN_PROGRESS', 'CLOSED', 'SPAM'];

function formatDate(s: string) {
  return new Date(s).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'NEW'
      ? 'bg-blue-100 text-blue-700'
      : status === 'CLOSED' || status === 'SPAM'
        ? 'bg-muted text-muted-foreground'
        : 'bg-amber-100 text-amber-700';
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${tone}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm text-foreground break-words">{value}</div>
    </div>
  );
}

/* ---------------------------------------------------------------- Discovery */
function DiscoveryTab() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['leads', 'briefs'],
    queryFn: () => leadsApi.listBriefs({ limit: 50 }),
  });

  const detail = useQuery({
    queryKey: ['leads', 'brief', openId],
    queryFn: () => leadsApi.getBrief(openId as string),
    enabled: !!openId,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ProjectBriefStatus }) =>
      leadsApi.updateBriefStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads', 'briefs'] });
      qc.invalidateQueries({ queryKey: ['leads', 'brief', openId] });
    },
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorState />;
  const rows = data?.data ?? [];
  if (rows.length === 0) return <EmptyState label="No discovery-call briefs yet." />;

  return (
    <>
      <Table head={['Name', 'Email', 'Service', 'Files', 'Status', 'Submitted', '']}>
        {rows.map((r: ProjectBriefSummary) => (
          <tr key={r.id} className="border-t border-border hover:bg-muted">
            <td className="px-4 py-3 font-medium text-foreground">{r.fullName}</td>
            <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
            <td className="px-4 py-3 text-muted-foreground">{r.serviceNeeded.replace(/_/g, ' ')}</td>
            <td className="px-4 py-3 text-muted-foreground">{r.attachmentCount || '—'}</td>
            <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
            <td className="px-4 py-3 text-muted-foreground">{formatDate(r.createdAt)}</td>
            <td className="px-4 py-3 text-right">
              <button onClick={() => setOpenId(r.id)} className="text-xs text-blue-600 hover:underline">
                View
              </button>
            </td>
          </tr>
        ))}
      </Table>

      {openId && (
        <Drawer onClose={() => setOpenId(null)} title="Discovery-call brief">
          {detail.isLoading || !detail.data ? (
            <Loading />
          ) : (
            <BriefDetailView
              brief={detail.data}
              onStatus={(status) => statusMutation.mutate({ id: detail.data!.id, status })}
              saving={statusMutation.isPending}
            />
          )}
        </Drawer>
      )}
    </>
  );
}

function BriefDetailView({
  brief,
  onStatus,
  saving,
}: {
  brief: ProjectBriefDetail;
  onStatus: (s: ProjectBriefStatus) => void;
  saving: boolean;
}) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name" value={brief.fullName} />
        <Field label="Email" value={brief.email} />
        <Field label="Service" value={brief.serviceNeeded.replace(/_/g, ' ')} />
        <Field label="Product of interest" value={brief.productOfInterest} />
        <Field label="Brand" value={brief.brand} />
        <Field label="Industry" value={brief.industry} />
        <Field label="Instagram / social" value={brief.instagram} />
        <Field label="Other links" value={brief.otherLinks} />
        <Field label="Reference link" value={brief.referenceLink} />
        <Field label="Submitted" value={formatDate(brief.createdAt)} />
      </div>

      {brief.brief && (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Message</div>
          <p className="whitespace-pre-wrap text-sm text-foreground">{brief.brief}</p>
        </div>
      )}

      {brief.attachments?.length > 0 && (
        <div>
          <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">Attachments</div>
          <ul className="space-y-1">
            {brief.attachments.map((a) => (
              <li key={a.id}>
                <a href={a.downloadUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                  {a.fileName}
                </a>{' '}
                <span className="text-xs text-muted-foreground">({Math.round(a.sizeBytes / 1024)} KB)</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <StatusEditor current={brief.status} options={BRIEF_STATUSES} onChange={(s) => onStatus(s as ProjectBriefStatus)} saving={saving} />
    </div>
  );
}

/* ---------------------------------------------------------------- Contacts */
function ContactTab({ category, emptyLabel }: { category: 'WAITLIST' | 'INQUIRY'; emptyLabel: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<ContactSubmission | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['leads', 'contacts', category],
    queryFn: () => leadsApi.listContacts({ category, limit: 50 }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ContactStatus }) => leadsApi.updateContactStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads', 'contacts', category] }),
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorState />;
  const rows = data?.data ?? [];
  if (rows.length === 0) return <EmptyState label={emptyLabel} />;

  const isWaitlist = category === 'WAITLIST';

  return (
    <>
      <Table
        head={
          isWaitlist
            ? ['Name', 'Email', 'Brand', 'Product', 'Status', 'Submitted', '']
            : ['Name', 'Email', 'Message', 'Status', 'Submitted', '']
        }
      >
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-border hover:bg-muted">
            <td className="px-4 py-3 font-medium text-foreground">{r.name}</td>
            <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
            {isWaitlist ? (
              <>
                <td className="px-4 py-3 text-muted-foreground">{r.brand || '—'}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.productOfInterest || '—'}</td>
              </>
            ) : (
              <td className="max-w-xs truncate px-4 py-3 text-muted-foreground">{r.message}</td>
            )}
            <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
            <td className="px-4 py-3 text-muted-foreground">{formatDate(r.createdAt)}</td>
            <td className="px-4 py-3 text-right">
              <button onClick={() => setOpen(r)} className="text-xs text-blue-600 hover:underline">
                View
              </button>
            </td>
          </tr>
        ))}
      </Table>

      {open && (
        <Drawer onClose={() => setOpen(null)} title={isWaitlist ? 'Waitlist entry' : 'Enquiry'}>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" value={open.name} />
              <Field label="Email" value={open.email} />
              <Field label="Phone" value={open.phone} />
              <Field label="Brand" value={open.brand} />
              <Field label="Industry" value={open.industry} />
              <Field label="Product of interest" value={open.productOfInterest} />
              <Field label="Submitted" value={formatDate(open.createdAt)} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Message</div>
              <p className="whitespace-pre-wrap text-sm text-foreground">{open.message}</p>
            </div>
            <StatusEditor
              current={open.status}
              options={CONTACT_STATUSES}
              saving={statusMutation.isPending}
              onChange={(status) =>
                statusMutation.mutate(
                  { id: open.id, status: status as ContactStatus },
                  { onSuccess: () => setOpen({ ...open, status: status as ContactStatus }) },
                )
              }
            />
          </div>
        </Drawer>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- Shared UI */
function StatusEditor({
  current,
  options,
  onChange,
  saving,
}: {
  current: string;
  options: string[];
  onChange: (s: string) => void;
  saving: boolean;
}) {
  return (
    <div className="flex items-center gap-3 border-t border-border pt-4">
      <label className="text-xs uppercase tracking-wide text-muted-foreground">Status</label>
      <select
        value={current}
        disabled={saving}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-50"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
      {saving && <span className="text-xs text-muted-foreground">Saving…</span>}
    </div>
  );
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            {head.map((h, i) => (
              <th key={i} className="px-4 py-3 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Drawer({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div
        className="h-full w-full max-w-lg overflow-y-auto bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const Loading = () => <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>;
const ErrorState = () => (
  <div className="py-16 text-center text-sm text-red-500">Couldn't load leads. Please try again.</div>
);
const EmptyState = ({ label }: { label: string }) => (
  <div className="py-16 text-center text-sm text-muted-foreground">{label}</div>
);

/* ---------------------------------------------------------------- Page */
export default function Leads() {
  const [tab, setTab] = useState<TabKey>('call');

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Leads</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Marketing-site submissions — discovery-call briefs, waitlist sign-ups, and general enquiries.
        </p>
      </header>

      <div className="mb-6 flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.key
                ? 'border-gray-900 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'call' && <DiscoveryTab />}
      {tab === 'waitlist' && <ContactTab category="WAITLIST" emptyLabel="No waitlist sign-ups yet." />}
      {tab === 'general' && <ContactTab category="INQUIRY" emptyLabel="No general enquiries yet." />}
    </div>
  );
}
